import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ApiError } from '@/shared/api/client'
import * as authApi from '@/shared/api/auth'
import type { MeResponse } from '@/shared/api/types'

export const useAuthStore = defineStore('auth', () => {
    const user = ref<MeResponse | null>(null)
    const loaded = ref(false)
    const loading = ref(false)

    const isAuthenticated = computed(() => user.value !== null)
    const csrfToken = computed(() => user.value?.csrf_token ?? null)
    const roles = computed(() => user.value?.roles ?? [])
    const flags = computed(() => user.value?.flags ?? [])

    // Access tiers drive the navbar persona system:
    //   0 — anonymous
    //   1 — signed in (public site + personal space)
    //   3 — master_admin / system_admin (full control persona)
    // system_admin is the elevated single-holder role on top of tier 3.
    // (Tier 2 belonged to the retired 'admin' role.)
    const isElevated = computed(() => roles.value.includes('system_admin'))
    const tier = computed(() => {
        if (!user.value) return 0
        if (roles.value.includes('master_admin') || isElevated.value) return 3
        return 1
    })

    function hasRole(name: string): boolean {
        return roles.value.includes(name)
    }

    function hasAnyRole(names: string[]): boolean {
        return names.some(hasRole)
    }

    function hasFlag(key: string): boolean {
        return flags.value.includes(key)
    }

    async function refresh(): Promise<void> {
        loading.value = true
        try {
            user.value = await authApi.me()
        } catch (err) {
            if (err instanceof ApiError && err.status === 401) {
                user.value = null
            } else {
                console.error('auth refresh failed', err)
            }
        } finally {
            loaded.value = true
            loading.value = false
        }
    }

    async function logout(): Promise<void> {
        try {
            await authApi.logout()
        } finally {
            user.value = null
        }
    }

    return {
        user,
        loaded,
        loading,
        isAuthenticated,
        csrfToken,
        roles,
        flags,
        tier,
        isElevated,
        hasRole,
        hasAnyRole,
        hasFlag,
        refresh,
        logout,
    }
})
